// Angle calculation utilities for angle dimensions

import { IsoPoint, Line, Arc, DrawingElement } from '@/app/types/cad';

/**
 * Calculate angle between two vectors defined by three points
 * @param vertex - Center point (Scheitelpunkt)
 * @param p1 - First arm endpoint
 * @param p2 - Second arm endpoint
 * @returns Angle in degrees (0° - 180°)
 */
export function calculateAngle(vertex: IsoPoint, p1: IsoPoint, p2: IsoPoint): number {
  // Vectors from vertex to endpoints
  const v1 = { x: p1.u - vertex.u, y: p1.v - vertex.v };
  const v2 = { x: p2.u - vertex.u, y: p2.v - vertex.v };
  
  // Dot product and magnitudes
  const dot = v1.x * v2.x + v1.y * v2.y;
  const mag1 = Math.sqrt(v1.x ** 2 + v1.y ** 2);
  const mag2 = Math.sqrt(v2.x ** 2 + v2.y ** 2);
  
  // Avoid division by zero
  if (mag1 === 0 || mag2 === 0) return 0;
  
  // Calculate angle in radians, then convert to degrees
  const angleRad = Math.acos(Math.max(-1, Math.min(1, dot / (mag1 * mag2))));
  return angleRad * (180 / Math.PI);
}

/**
 * Convert angle from degrees to radians
 */
export function degToRad(degrees: number): number {
  return degrees * (Math.PI / 180);
}

/**
 * Convert angle from radians to degrees
 */
export function radToDeg(radians: number): number {
  return radians * (180 / Math.PI);
}

/**
 * Convert angle from degrees to gradians
 */
export function degToGrad(degrees: number): number {
  return degrees * (10 / 9);
}

/**
 * Convert angle from gradians to degrees
 */
export function gradToDeg(gradians: number): number {
  return gradians * (9 / 10);
}

/**
 * Find intersection point between two lines
 * Returns null if lines are parallel
 */
export function findLineIntersection(
  line1: Line,
  line2: Line
): IsoPoint | null {
  const x1 = line1.start.u;
  const y1 = line1.start.v;
  const x2 = line1.end.u;
  const y2 = line1.end.v;
  
  const x3 = line2.start.u;
  const y3 = line2.start.v;
  const x4 = line2.end.u;
  const y4 = line2.end.v;
  
  const denominator = (x1 - x2) * (y3 - y4) - (y1 - y2) * (x3 - x4);
  
  // Lines are parallel or coincident
  if (Math.abs(denominator) < 1e-10) {
    return null;
  }
  
  const t = ((x1 - x3) * (y3 - y4) - (y1 - y3) * (x3 - x4)) / denominator;
  const u = -((x1 - x2) * (y1 - y3) - (y1 - y2) * (x1 - x3)) / denominator;
  
  // Check if intersection point lies on both line segments
  if (t >= 0 && t <= 1 && u >= 0 && u <= 1) {
    return {
      u: x1 + t * (x2 - x1),
      v: y1 + t * (y2 - y1),
    };
  }
  
  // Lines intersect, but not within both segments
  // Return theoretical intersection for angle measurement
  return {
    u: x1 + t * (x2 - x1),
    v: y1 + t * (y2 - y1),
  };
}

/**
 * Check if a point is close to a line endpoint
 * @param point - Point to check
 * @param line - Line to check against
 * @param tolerance - Distance tolerance in grid units
 * @returns Object with info about which endpoint was hit, or null
 */
export function hitTestLineEndpoint(
  point: IsoPoint,
  line: Line,
  tolerance: number = 0.5
): { endpoint: 'start' | 'end'; point: IsoPoint } | null {
  const distToStart = Math.sqrt(
    (point.u - line.start.u) ** 2 + (point.v - line.start.v) ** 2
  );
  
  if (distToStart <= tolerance) {
    return { endpoint: 'start', point: line.start };
  }
  
  const distToEnd = Math.sqrt(
    (point.u - line.end.u) ** 2 + (point.v - line.end.v) ** 2
  );
  
  if (distToEnd <= tolerance) {
    return { endpoint: 'end', point: line.end };
  }
  
  return null;
}

/**
 * Find the common endpoint between two lines
 * Returns null if lines don't share an endpoint
 */
export function findCommonEndpoint(line1: Line, line2: Line): IsoPoint | null {
  const tolerance = 0.1; // Small tolerance for floating point comparison
  
  const samePoint = (p1: IsoPoint, p2: IsoPoint) =>
    Math.abs(p1.u - p2.u) < tolerance && Math.abs(p1.v - p2.v) < tolerance;
  
  if (samePoint(line1.start, line2.start) || samePoint(line1.start, line2.end)) {
    return line1.start;
  }
  
  if (samePoint(line1.end, line2.start) || samePoint(line1.end, line2.end)) {
    return line1.end;
  }
  
  return null;
}

/**
 * Get the other endpoint of a line (not the vertex)
 */
export function getOtherEndpoint(line: Line, vertex: IsoPoint): IsoPoint {
  const tolerance = 0.1;
  const samePoint = (p1: IsoPoint, p2: IsoPoint) =>
    Math.abs(p1.u - p2.u) < tolerance && Math.abs(p1.v - p2.v) < tolerance;
  
  if (samePoint(line.start, vertex)) {
    return line.end;
  }
  return line.start;
}

/**
 * Determine optimal arc radius for angle dimension
 * Based on the distance from vertex to the endpoints
 */
export function calculateOptimalArcRadius(
  vertex: IsoPoint,
  p1: IsoPoint,
  p2: IsoPoint
): number {
  const dist1 = Math.sqrt((p1.u - vertex.u) ** 2 + (p1.v - vertex.v) ** 2);
  const dist2 = Math.sqrt((p2.u - vertex.u) ** 2 + (p2.v - vertex.v) ** 2);
  
  // Use 30% of the minimum distance to endpoints
  const minDist = Math.min(dist1, dist2);
  return Math.max(0.5, minDist * 0.3);
}
